import { useQueries } from '@tanstack/react-query'
import { useAuth } from '@wehunt/core-web'
import type { Hunt } from '@wehunt/ui'
import { useApiFetch } from '../../shared/api'
import { type GroupHuntsResponse, groupUpcomingDtoToHunt } from './useGroupHunts'
import { ongoingDtoToHunt } from './useOngoingHunts'

/**
 * CLUB-272 — hunts belonging to the user's teams that they are NOT part of
 * (not invited / not a participant), so a team member can still see what their
 * team has planned or ongoing. Read-only visibility; scoped to the caller's own
 * groups (each request is membership-gated server-side — see CLUB-250).
 *
 * Reuses the same query key as {@link useGroupHunts} so team-detail views and
 * the calendar share one cache entry per group.
 */
export function useTeamHunts(groupIds: string[]): { hunts: Hunt[]; isLoading: boolean } {
  const call = useApiFetch()
  const { state } = useAuth()
  const uid = state.status === 'signed-in' ? state.user.uid : undefined

  const results = useQueries({
    queries: groupIds.map((groupId) => ({
      queryKey: ['clubhouse', 'groups', groupId, 'hunts', uid],
      queryFn: () => call<GroupHuntsResponse>(`/clubhouse/groups/${groupId}/hunts`),
      enabled: !!uid,
      staleTime: 60 * 1000,
    })),
  })

  const hunts = results.flatMap((r) => [
    ...(r.data?.upcoming ?? [])
      .filter((h) => !h.isParticipant)
      .map(groupUpcomingDtoToHunt),
    // Live team hunts. The ongoing DTO carries no isParticipant flag (it is the
    // ended shape), so participant de-duplication against the caller's own
    // ongoing feed happens in the consumer, keyed by hunt id.
    ...(r.data?.ongoing ?? []).map(ongoingDtoToHunt),
  ])
  const isLoading = results.some((r) => r.isLoading)

  return { hunts, isLoading }
}
